// src/components/legal/CookiePolicy.tsx
import Footer from "@/components/shared/Footer";
import {
  ArrowLeft,
  BarChart3,
  Cookie,
  Eye,
  Settings,
  Shield,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

export default function CookiePolicy() {
  const lastUpdated = "September 2025";
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600/20 via-yellow-600/20 to-orange-600/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 py-16">
          <Link
            to="/"
            className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>

          <div className="flex items-center mb-6">
            <Cookie className="w-8 h-8 text-orange-400 mr-4" />
            <h1 className="text-4xl lg:text-5xl font-bold text-white">
              Cookie Policy
            </h1>
          </div>

          <p className="text-xl text-gray-300 max-w-3xl">
            How we use cookies and similar technologies to improve your
            experience
          </p>
          <p className="text-sm text-gray-400 mt-4">
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-600/50 p-8 space-y-8">
          {/* What Are Cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              What Are Cookies?
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <p className="mb-4">
                Cookies are small text files stored on your device when you
                visit our website. They help us remember your preferences,
                analyse how you use our site, and provide a more personalised
                experience.
              </p>
              <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-500/20">
                <p className="text-orange-300 text-sm">
                  <strong>Good to know:</strong> You have control over cookies.
                  Most cookies improve your experience, but none are used to
                  personally identify you without consent.
                </p>
              </div>
            </div>
          </section>

          {/* Cookie Categories */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-6">
              Types of Cookies We Use
            </h2>

            <div className="space-y-4">
              {/* Essential Cookies */}
              <div className="bg-slate-700/30 rounded-lg border border-green-500/20">
                <button
                  onClick={() => toggleSection("essential")}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-700/40 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-green-400" />
                    <div>
                      <h3 className="font-semibold text-green-400">
                        Essential Cookies
                      </h3>
                      <p className="text-sm text-gray-400">
                        Required for basic site functionality
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded">
                      Required
                    </span>
                    <span
                      className={`transform transition-transform ${
                        expandedSection === "essential" ? "rotate-180" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </div>
                </button>

                {expandedSection === "essential" && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-600">
                            <th className="text-left py-2 text-green-400">
                              Purpose
                            </th>
                            <th className="text-left py-2 text-green-400">
                              Expires
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-300">
                          <tr>
                            <td className="py-2">
                              Session management and security
                            </td>
                            <td className="py-2">Session end</td>
                          </tr>
                          <tr>
                            <td className="py-2">
                              Culture Lab progress tracking
                            </td>
                            <td className="py-2">24 hours</td>
                          </tr>
                          <tr>
                            <td className="py-2">Form data preservation</td>
                            <td className="py-2">Session end</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-400">
                      These cookies cannot be disabled as they're essential for
                      the website to function.
                    </p>
                  </div>
                )}
              </div>

              {/* Analytics Cookies */}
              <div className="bg-slate-700/30 rounded-lg border border-blue-500/20">
                <button
                  onClick={() => toggleSection("analytics")}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-700/40 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    <div>
                      <h3 className="font-semibold text-blue-400">
                        Analytics Cookies
                      </h3>
                      <p className="text-sm text-gray-400">
                        Help us understand site usage
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded">
                      Optional
                    </span>
                    <span
                      className={`transform transition-transform ${
                        expandedSection === "analytics" ? "rotate-180" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </div>
                </button>

                {expandedSection === "analytics" && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-600">
                            <th className="text-left py-2 text-blue-400">
                              Purpose
                            </th>
                            <th className="text-left py-2 text-blue-400">
                              Expires
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-300">
                          <tr>
                            <td className="py-2">
                              Google Analytics - user behaviour analysis
                            </td>
                            <td className="py-2">2 years</td>
                          </tr>
                          <tr>
                            <td className="py-2">
                              Culture Lab completion tracking
                            </td>
                            <td className="py-2">6 months</td>
                          </tr>
                          <tr>
                            <td className="py-2">Performance monitoring</td>
                            <td className="py-2">30 days</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-400">
                      All analytics data is anonymised and helps us improve the
                      platform.
                    </p>
                  </div>
                )}
              </div>

              {/* Functional Cookies */}
              <div className="bg-slate-700/30 rounded-lg border border-purple-500/20">
                <button
                  onClick={() => toggleSection("functional")}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-700/40 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-purple-400" />
                    <div>
                      <h3 className="font-semibold text-purple-400">
                        Functional Cookies
                      </h3>
                      <p className="text-sm text-gray-400">
                        Enhanced features and preferences
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                      Optional
                    </span>
                    <span
                      className={`transform transition-transform ${
                        expandedSection === "functional" ? "rotate-180" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </div>
                </button>

                {expandedSection === "functional" && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-600">
                            <th className="text-left py-2 text-purple-400">
                              Purpose
                            </th>
                            <th className="text-left py-2 text-purple-400">
                              Expires
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-300">
                          <tr>
                            <td className="py-2">User interface preferences</td>
                            <td className="py-2">1 year</td>
                          </tr>
                          <tr>
                            <td className="py-2">Form auto-fill data</td>
                            <td className="py-2">30 days</td>
                          </tr>
                          <tr>
                            <td className="py-2">Service personalisation</td>
                            <td className="py-2">6 months</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-400">
                      These cookies make our services more convenient but aren't
                      essential.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Managing Cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              Managing Your Cookie Preferences
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
                <h3 className="font-semibold text-blue-400 mb-2 flex items-center">
                  <Settings className="w-4 h-4 mr-2" />
                  Browser Settings
                </h3>
                <p className="text-gray-300 text-sm mb-3">
                  Most browsers allow you to block or delete cookies through
                  their settings. Note that some site features may not work
                  properly without cookies.
                </p>
                <div className="text-xs text-gray-400">
                  Chrome • Firefox • Safari • Edge
                </div>
              </div>

              <div className="bg-slate-700/30 rounded-lg p-4 border border-purple-500/20">
                <h3 className="font-semibold text-purple-400 mb-2 flex items-center">
                  <Eye className="w-4 h-4 mr-2" />
                  Our Commitment
                </h3>
                <p className="text-gray-300 text-sm mb-3">
                  We only use cookies that improve your experience. We don't
                  sell your data or use tracking for advertising purposes.
                </p>
                <p className="text-xs text-gray-400">Privacy-first approach</p>
              </div>
            </div>
          </section>

          {/* Updates */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              Policy Updates
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <p className="mb-4">
                We may update this cookie policy when we add new features or
                change our practices. Significant changes will be announced via
                website notification.
              </p>
              <div className="bg-slate-700/30 rounded-lg p-4 border border-orange-500/20">
                <p className="text-orange-300 text-sm">
                  <strong>Questions about cookies?</strong> Contact us at
                  hello@thecontentgroup.co or review our{" "}
                  <Link
                    to="/privacy-policy"
                    className="underline hover:text-orange-200"
                  >
                    Privacy Policy
                  </Link>
                  for more information.
                </p>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-slate-600/50 pt-6">
            <div className="flex items-center justify-between text-sm text-gray-400">
              <div className="flex items-center">
                <Cookie className="w-4 h-4 mr-2" />
                Last updated: {lastUpdated}
              </div>
              <div className="flex space-x-4">
                <Link
                  to="/terms-conditions"
                  className="hover:text-white transition-colors"
                >
                  Terms & Conditions
                </Link>
                <Link
                  to="/privacy-policy"
                  className="hover:text-white transition-colors"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Your existing Footer */}
      <Footer variant="dark" />
    </div>
  );
}
